/**
 * Create and edit a tenant.
 *
 * Two things are deliberately not on the main form:
 *
 *   SSN, DOB, licence, screening notes → `SensitivePanel`, a separate request to a
 *   separate table that owners cannot reach and MAINTENANCE staff cannot read.
 *
 *   Forwarding address → its own endpoint and its own panel, because it is the
 *   MCL 554.609 damages-notice destination rather than an ordinary contact field.
 *   The 30-day clock runs from move-out whether or not it was filled in, so
 *   recording it is an event worth finding in the audit log on its own.
 *
 * ---------------------------------------------------------------------------
 * Layout
 * ---------------------------------------------------------------------------
 *
 * Same shape as OwnerForm.tsx's identity-first page, sharing its
 * `.person-profile`/`.person-layout` CSS: a profile card up top (photo,
 * name, contact, at-a-glance pills) with the photo upload as a badge on the
 * avatar itself, then a two-column body. The main column gets the edit form
 * plus the two panels that are operational tools a staff member actively
 * uses (forwarding address, ACH access) — not just data on file, so they
 * stay prominent rather than in the sidebar. The sidebar holds the two
 * genuinely administrative panels: the sensitive record and the file list.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useForm, useInvalidate, useList } from '@refinedev/core';
import { Camera } from 'lucide-react';
import { api } from '../../../lib/api.js';
import { useCan } from '../../../lib/capabilities.js';
import { useMessaging } from '../../../lib/messaging-context.js';
import { fetchSmsConsent, type SmsConsentDto } from '../../../lib/sms-consent.js';
import { Avatar } from '../../../components/Avatar.js';
import {
  Field,
  FormErrors,
  TextInput,
  fieldErrors,
  humanizeEnum,
} from '../../../components/Form.js';
import { SensitivePanel } from '../../../components/SensitivePanel.js';
import { SmsConsentDialog } from '../../../components/SmsConsentDialog.js';
import { DocumentList } from '../../../components/DocumentList.js';
import { PortalAccessCard } from '../../../components/PortalAccessCard.js';
import { PhotoUpload } from '../../../components/PhotoUpload.js';

interface TenantRecord {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  forwardingAddress: {
    line1: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
  };
  achAccess: {
    revokedAt: string | null;
    revokedReason: string | null;
  };
  leaseCount: number;
  hasPortalAccount: boolean;
  photoUrl: string | null;
}

interface TenantFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
}

const EMPTY: TenantFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
};

export function TenantForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const canText = useCan('messaging:write');
  const { openConversationForTenant } = useMessaging();

  const [form, setForm] = useState<TenantFormState>(EMPTY);

  const { onFinish, query, mutation } = useForm<TenantRecord>({
    resource: 'tenants',
    action: isEdit ? 'edit' : 'create',
    ...(isEdit ? { id } : {}),
    redirect: false,
  });

  const record = query?.data?.data;

  useEffect(() => {
    if (!record) return;
    setForm({
      firstName: record.firstName,
      lastName: record.lastName,
      email: record.email,
      phone: record.phone ?? '',
      emergencyContactName: record.emergencyContactName ?? '',
      emergencyContactPhone: record.emergencyContactPhone ?? '',
    });
  }, [record?.id, record]);

  const set =
    <K extends keyof TenantFormState>(key: K) =>
    (value: TenantFormState[K]) =>
      setForm((previous) => ({ ...previous, [key]: value }));

  const errors = fieldErrors(mutation.error);

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone || null,
      emergencyContactName: form.emergencyContactName || null,
      emergencyContactPhone: form.emergencyContactPhone || null,
    };

    const saved = (await onFinish(payload)) as { data?: TenantRecord } | undefined;
    const savedId = saved?.data?.id ?? id;
    if (savedId && !isEdit) void navigate(`/staff/tenants/${savedId}/edit`);
    else if (!savedId) void navigate('/staff/tenants');
  }

  if (isEdit && query?.isLoading) return <p className="muted">Loading…</p>;

  const formCard = (
    <form className="stack card" onSubmit={(event) => void submit(event)}>
      <FormErrors error={mutation.error} />

      <p className="form-section-label">Contact</p>

      <Field label="First name" name="firstName" required error={errors.firstName}>
        <TextInput name="firstName" value={form.firstName} onChange={set('firstName')} required />
      </Field>

      <Field label="Last name" name="lastName" required error={errors.lastName}>
        <TextInput name="lastName" value={form.lastName} onChange={set('lastName')} required />
      </Field>

      <Field label="Email" name="email" required error={errors.email}>
        <TextInput name="email" type="email" value={form.email} onChange={set('email')} required />
      </Field>

      <Field label="Phone" name="phone" error={errors.phone}>
        <TextInput name="phone" type="tel" value={form.phone} onChange={set('phone')} />
      </Field>

      <p className="form-section-label">Emergency contact</p>

      <Field label="Name" name="emergencyContactName">
        <TextInput
          name="emergencyContactName"
          value={form.emergencyContactName}
          onChange={set('emergencyContactName')}
        />
      </Field>

      <Field label="Phone" name="emergencyContactPhone">
        <TextInput
          name="emergencyContactPhone"
          type="tel"
          value={form.emergencyContactPhone}
          onChange={set('emergencyContactPhone')}
        />
      </Field>

      <div className="row">
        <button type="submit" className="button" disabled={mutation.isLoading}>
          {mutation.isLoading ? 'Saving…' : isEdit ? 'Save changes' : 'Create tenant'}
        </button>
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff/tenants')}
        >
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <section className="stack">
      <div className="row">
        <span className="shell__spacer" />
        {isEdit && record?.phone && canText ? (
          <button
            type="button"
            className="button button--quiet"
            onClick={() =>
              void openConversationForTenant({
                id: record.id,
                name: `${record.firstName} ${record.lastName}`.trim() || 'Tenant',
              })
            }
          >
            Text
          </button>
        ) : null}
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff/tenants')}
        >
          ← All tenants
        </button>
      </div>

      {isEdit && record && id ? (
        <>
          <TenantProfileCard record={record} tenantId={id} />

          <div className="person-layout">
            <div className="stack">
              {formCard}
              <ForwardingAddressPanel tenantId={id} current={record.forwardingAddress} />
              <AchAccessPanel tenantId={id} current={record.achAccess} />
              {canText ? (
                <SmsConsentPanel
                  tenantId={id}
                  tenantName={`${record.firstName} ${record.lastName}`.trim() || 'Tenant'}
                />
              ) : null}
              <DependentsPanel tenantId={id} />
              <PetsPanel tenantId={id} />
            </div>
            <div className="stack">
              <PortalAccessCard
                resource="tenants"
                id={id}
                hasPortalAccount={record.hasPortalAccount}
                email={record.email}
              />
              <SensitivePanel resource="tenants" id={id} />
              <FilesCard tenantId={id} />
            </div>
          </div>
        </>
      ) : (
        <>
          <h1>Add tenant</h1>
          {formCard}
        </>
      )}
    </section>
  );
}

/**
 * The file list plus a general-purpose uploader — same shape as
 * PropertyShow.tsx/UnitShow.tsx's own Files section, and OwnerForm.tsx's.
 * The profile card's camera badge is a shortcut for "set this tenant's
 * photo" specifically; this is for anything else attached to their record,
 * defaulting to "Other" since it isn't the photo.
 */
function FilesCard({ tenantId }: { tenantId: string }) {
  const invalidate = useInvalidate();
  return (
    <div className="card stack">
      <h2 style={{ margin: 0 }}>Files</h2>
      <DocumentList filter={{ tenantId }} canDelete />
      <details>
        <summary>Upload a file</summary>
        <PhotoUpload
          parent={{ tenantId }}
          defaultDocumentType="OTHER"
          onUploaded={() => {
            void invalidate({ resource: 'documents', invalidates: ['list'] });
            void invalidate({ resource: 'tenants', invalidates: ['detail'] });
          }}
        />
      </details>
    </div>
  );
}

/**
 * Identity at a glance: photo, name, contact, and the two facts staff check
 * most often (how many leases, whether ACH is currently revoked). `Camera`
 * badge toggles the upload form inline, same pattern as OwnerForm.tsx.
 */
function TenantProfileCard({ record, tenantId }: { record: TenantRecord; tenantId: string }) {
  const invalidate = useInvalidate();
  const [showUpload, setShowUpload] = useState(false);
  const name = `${record.firstName} ${record.lastName}`.trim();

  return (
    <div className="stack">
      <div className="card person-profile">
        <div className="person-profile__avatar">
          <Avatar name={name} photoUrl={record.photoUrl} size="lg" />
          <button
            type="button"
            className="person-profile__avatar-edit"
            onClick={() => setShowUpload((v) => !v)}
            aria-label={record.photoUrl ? 'Change photo' : 'Add a photo'}
            title={record.photoUrl ? 'Change photo' : 'Add a photo'}
          >
            <Camera size={13} strokeWidth={2.2} />
          </button>
        </div>

        <div className="person-profile__info">
          <h1>{name || 'Tenant'}</h1>
          <span className="muted">{record.email}</span>
          {record.phone ? <span className="muted">{record.phone}</span> : null}
          <div className="row" style={{ marginTop: '0.35rem' }}>
            <LeasePill tenantId={tenantId} leaseCount={record.leaseCount} />
            {record.achAccess.revokedAt ? (
              <span className="pill pill--late">ACH revoked</span>
            ) : null}
            {record.hasPortalAccount ? (
              <span className="pill">Has login</span>
            ) : (
              <span className="muted">No login</span>
            )}
          </div>
        </div>
      </div>

      {showUpload ? (
        <div className="card">
          <PhotoUpload
            parent={{ tenantId }}
            defaultDocumentType="TENANT_PHOTO"
            onUploaded={() => {
              void invalidate({ resource: 'documents', invalidates: ['list'] });
              void invalidate({ resource: 'tenants', invalidates: ['detail'] });
              setShowUpload(false);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

interface LeaseSummary {
  id: string;
  unitLabel: string;
  propertyName: string;
  status: string;
}

/**
 * The lease-count pill, made to actually go somewhere. One lease jumps
 * straight to it — no reason to make a single-item menu someone has to
 * open first. More than one opens a small menu (same positioning technique
 * as SettingsPanel.tsx's gear-icon dropdown: relative wrapper, absolute
 * panel, outside-click/Escape closes it) so staff pick which lease before
 * committing to a page. `leaseCount` comes from the tenant record we
 * already have — used for the label immediately, so it doesn't wait on
 * this component's own fetch of the leases themselves.
 */
function LeasePill({ tenantId, leaseCount }: { tenantId: string; leaseCount: number }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { data } = useList<LeaseSummary>({
    resource: 'leases',
    filters: [{ field: 'tenantId', operator: 'eq', value: tenantId }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: leaseCount > 0 },
  });

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const label = `${leaseCount} lease${leaseCount === 1 ? '' : 's'}`;

  if (leaseCount === 0) return <span className="pill">{label}</span>;

  const leases = data?.data ?? [];

  if (leaseCount === 1) {
    const only = leases[0];
    // Still loading, or (should not happen) the count and the fetch
    // disagree — a plain pill is the safe fallback either way.
    return only ? (
      <Link to={`/staff/leases/${only.id}`} className="pill">
        {label}
      </Link>
    ) : (
      <span className="pill">{label}</span>
    );
  }

  return (
    <div className="pill-menu" ref={menuRef}>
      <button
        type="button"
        className="pill pill-menu__trigger"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
      </button>

      {open && leases.length > 0 ? (
        <div className="pill-menu__panel card">
          {leases.map((lease) => (
            <Link
              key={lease.id}
              to={`/staff/leases/${lease.id}`}
              className="pill-menu__item"
              onClick={() => setOpen(false)}
            >
              <strong>{lease.unitLabel}</strong>
              <span className="muted">
                {' '}
                · {lease.propertyName} — {humanizeEnum(lease.status)}
              </span>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Revoke or restore ACH access, after a returned payment. Applies across
 * every lease this tenant is named on — a bounced bank account is a property
 * of the account, not of the lease the return happened to land on. Read by
 * the portal's Pay rent page; Stripe itself isn't wired yet (step 6), so this
 * only gates that page's copy for now.
 */
function AchAccessPanel({
  tenantId,
  current,
}: {
  tenantId: string;
  current: { revokedAt: string | null; revokedReason: string | null };
}) {
  const invalidate = useInvalidate();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Seeded from the prop but not bound to it afterward: `<details open={…}>` is
  // controlled, and binding it straight to `revoked` snaps the panel shut the
  // instant a save flips that value — hiding the confirmation of the very
  // action staff just took. Manual toggling stays live; the initial state
  // still reflects reality on first load or navigation.
  const [open, setOpen] = useState(() => Boolean(current.revokedAt));

  const revoked = Boolean(current.revokedAt);

  async function save(nextRevoked: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api(`/tenants/${tenantId}/ach-access`, {
        method: 'PUT',
        body: JSON.stringify({ revoked: nextRevoked, reason: nextRevoked ? reason : null }),
      });
      setReason('');
      setOpen(true);
      // The parent form's record comes from Refine's query cache — without
      // this, `current` stays stale and the panel keeps showing the old
      // state until the page is reloaded.
      await invalidate({ resource: 'tenants', invalidates: ['detail'], id: tenantId });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="card" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>
        ACH access {revoked ? <span className="pill pill--late">Revoked</span> : null}
      </summary>

      {revoked ? (
        <div className="stack">
          <p className="alert alert--warn">
            Revoked {current.revokedAt?.slice(0, 10)}
            {current.revokedReason ? ` — ${current.revokedReason}` : ''}. The portal directs this
            tenant to pay by Cashier's Check or Wire Transfer instead.
          </p>
          <div className="row">
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => void save(false)}
            >
              {busy ? 'Saving…' : 'Restore ACH access'}
            </button>
          </div>
        </div>
      ) : (
        <div className="stack">
          <p className="muted">
            Blocks this tenant's ACH payment option across every lease they're on, and points them
            to the office instead. Use this after a returned payment.
          </p>
          <Field label="Reason" name="achReason" required>
            <TextInput
              name="achReason"
              value={reason}
              onChange={setReason}
              placeholder="e.g. Returned payment 2026-08-01, insufficient funds"
            />
          </Field>
          <div className="row">
            <button
              type="button"
              className="button"
              disabled={busy || !reason}
              onClick={() => void save(true)}
            >
              {busy ? 'Saving…' : 'Revoke ACH access'}
            </button>
          </div>
        </div>
      )}

      {error ? (
        <p className="alert alert--error">
          {error instanceof Error ? error.message : 'Could not update ACH access.'}
        </p>
      ) : null}
    </details>
  );
}

/**
 * SMS consent status — deliberately its own fetch, not part of `record`/
 * `TenantDto` (see SmsConsentDto's own doc comment in modules/tenants/
 * service.ts for why). "Manage SMS consent" opens the same dialog the
 * Text-button gate uses (mode="edit" here instead of "gate"), reachable at
 * any time independent of texting.
 */
function SmsConsentPanel({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const [consent, setConsent] = useState<SmsConsentDto | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(() => {
    fetchSmsConsent(tenantId)
      .then(setConsent)
      .catch((caught: unknown) => setLoadError(caught));
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const statusPill =
    consent?.status === 'DECLINED' ? (
      <span className="pill pill--late">Declined</span>
    ) : consent?.status === 'NOT_ASKED' ? (
      <span className="pill">Not asked</span>
    ) : consent?.status === 'CONSENTED' ? (
      <span className="pill pill--positive">Consented</span>
    ) : null;

  return (
    <details className="card">
      <summary>SMS consent {statusPill}</summary>

      {loadError ? (
        <p className="alert alert--error">Could not load SMS consent status.</p>
      ) : consent ? (
        <div className="stack">
          {consent.status !== 'NOT_ASKED' ? (
            <p className="muted">
              {consent.updatedAt ? consent.updatedAt.slice(0, 10) : ''}
              {consent.updatedByName ? ` — recorded by ${consent.updatedByName}` : ''}
              {consent.source === 'SURGE_WEBHOOK' ? ' (tenant replied via SMS)' : ''}
              {consent.consentedPhone ? ` for ${consent.consentedPhone}` : ''}
              {consent.reason ? `: "${consent.reason}"` : ''}
            </p>
          ) : (
            <p className="muted">This tenant hasn't been asked about SMS consent yet.</p>
          )}
          <div className="row">
            <button type="button" className="button" onClick={() => setDialogOpen(true)}>
              Manage SMS consent
            </button>
          </div>
        </div>
      ) : (
        <p className="muted">Loading…</p>
      )}

      {dialogOpen ? (
        <SmsConsentDialog
          tenantId={tenantId}
          tenantName={tenantName}
          mode="edit"
          onResolved={() => {
            setDialogOpen(false);
            load();
          }}
          onClose={() => setDialogOpen(false)}
        />
      ) : null}
    </details>
  );
}

/**
 * The forwarding address, on its own endpoint.
 *
 * Any two-letter state is accepted here — the only address in the system that is not
 * Michigan-pinned. A departed tenant may genuinely have left the state, and the
 * damages notice still has to reach them.
 */
function ForwardingAddressPanel({
  tenantId,
  current,
}: {
  tenantId: string;
  current: {
    line1: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
  };
}) {
  const [line1, setLine1] = useState(current.line1 ?? '');
  const [city, setCity] = useState(current.city ?? '');
  const [state, setState] = useState(current.state ?? 'MI');
  const [postalCode, setPostalCode] = useState(current.postalCode ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  async function save(clear: boolean) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api(`/tenants/${tenantId}/forwarding-address`, {
        method: 'PUT',
        body: JSON.stringify(clear ? null : { line1, city, state, postalCode }),
      });
      if (clear) {
        setLine1('');
        setCity('');
        setPostalCode('');
      }
      setSaved(true);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="card">
      <summary>Forwarding address (move-out)</summary>

      <p className="muted">
        Where the itemized damages notice goes. MCL 554.609 gives 30 days from termination of
        occupancy, and the clock runs whether or not this is filled in — missing it forfeits the
        right to withhold and exposes the company to double damages.
      </p>

      <div className="stack">
        <Field label="Street" name="fwdLine1">
          <TextInput name="fwdLine1" value={line1} onChange={setLine1} />
        </Field>
        <Field label="City" name="fwdCity">
          <TextInput name="fwdCity" value={city} onChange={setCity} />
        </Field>
        <Field label="State" name="fwdState" hint="Any state — a departed tenant may have left MI.">
          <TextInput name="fwdState" value={state} onChange={setState} />
        </Field>
        <Field label="ZIP" name="fwdPostalCode">
          <TextInput name="fwdPostalCode" value={postalCode} onChange={setPostalCode} />
        </Field>

        {error ? (
          <p className="alert alert--error">
            {error instanceof Error ? error.message : 'Could not save.'}
          </p>
        ) : null}
        {saved ? <p className="muted">Saved.</p> : null}

        <div className="row">
          <button
            type="button"
            className="button"
            disabled={busy || line1 === '' || city === '' || postalCode === ''}
            onClick={() => void save(false)}
          >
            {busy ? 'Saving…' : 'Save forwarding address'}
          </button>
          <button
            type="button"
            className="button button--quiet"
            disabled={busy}
            onClick={() => void save(true)}
          >
            Clear
          </button>
        </div>
      </div>
    </details>
  );
}

interface DependentRow {
  id: string;
  name: string;
  relationship: string;
  notes: string | null;
}

const EMPTY_DEPENDENT_FORM = { name: '', relationship: '', notes: '' };

/**
 * Non-leaseholder occupants — a child, a spouse, a roommate not named on
 * the lease. Staff-only for now (see the RLS policy comment on the
 * `dependents` table): no owner or tenant portal reads this yet.
 */
function DependentsPanel({ tenantId }: { tenantId: string }) {
  const canWrite = useCan('tenants:write');
  const invalidate = useInvalidate();
  const { data, isLoading } = useList<DependentRow>({
    resource: 'dependents',
    filters: [{ field: 'tenantId', operator: 'eq', value: tenantId }],
    pagination: { mode: 'off' },
  });

  const [form, setForm] = useState(EMPTY_DEPENDENT_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const dependents = data?.data ?? [];

  async function add(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/dependents', {
        method: 'POST',
        body: JSON.stringify({
          tenantId,
          name: form.name,
          relationship: form.relationship,
          notes: form.notes || null,
        }),
      });
      setForm(EMPTY_DEPENDENT_FORM);
      await invalidate({ resource: 'dependents', invalidates: ['list'] });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: DependentRow) {
    if (!window.confirm(`Remove ${row.name} from this tenant's household?`)) return;
    setError(null);
    try {
      await api(`/dependents/${row.id}`, { method: 'DELETE' });
      await invalidate({ resource: 'dependents', invalidates: ['list'] });
    } catch (caught) {
      setError(caught);
    }
  }

  return (
    <details className="card" open={dependents.length > 0}>
      <summary>Dependents{dependents.length > 0 ? ` (${dependents.length})` : ''}</summary>

      <div className="stack">
        {isLoading ? (
          <p className="muted">Loading…</p>
        ) : dependents.length === 0 ? (
          <p className="muted">No dependents on file.</p>
        ) : (
          dependents.map((dependent) => (
            <div key={dependent.id} className="row">
              <div className="stack" style={{ gap: '0.1rem' }}>
                <span>
                  <strong>{dependent.name}</strong>{' '}
                  <span className="muted">— {dependent.relationship}</span>
                </span>
                {dependent.notes ? <span className="muted">{dependent.notes}</span> : null}
              </div>
              <span className="shell__spacer" />
              {canWrite ? (
                <button
                  type="button"
                  className="button button--quiet"
                  onClick={() => void remove(dependent)}
                >
                  Remove
                </button>
              ) : null}
            </div>
          ))
        )}

        {canWrite ? (
          <form className="stack" onSubmit={(event) => void add(event)}>
            <Field label="Name" name="dependentName" required>
              <TextInput
                name="dependentName"
                value={form.name}
                onChange={(value) => setForm((f) => ({ ...f, name: value }))}
                required
              />
            </Field>
            <Field
              label="Relationship"
              name="dependentRelationship"
              required
              hint="Child, spouse, roommate…"
            >
              <TextInput
                name="dependentRelationship"
                value={form.relationship}
                onChange={(value) => setForm((f) => ({ ...f, relationship: value }))}
                required
              />
            </Field>
            <Field label="Notes" name="dependentNotes">
              <TextInput
                name="dependentNotes"
                value={form.notes}
                onChange={(value) => setForm((f) => ({ ...f, notes: value }))}
              />
            </Field>

            {error ? (
              <p className="alert alert--error">
                {error instanceof Error ? error.message : 'Could not save.'}
              </p>
            ) : null}

            <div className="row">
              <button
                type="submit"
                className="button"
                disabled={busy || form.name === '' || form.relationship === ''}
              >
                {busy ? 'Adding…' : 'Add dependent'}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </details>
  );
}

interface PetRow {
  id: string;
  name: string;
  species: string;
  breed: string | null;
  notes: string | null;
}

const EMPTY_PET_FORM = { name: '', species: '', breed: '', notes: '' };

/** Pets on file for a tenant. Same staff-only scope as DependentsPanel above. */
function PetsPanel({ tenantId }: { tenantId: string }) {
  const canWrite = useCan('tenants:write');
  const invalidate = useInvalidate();
  const { data, isLoading } = useList<PetRow>({
    resource: 'pets',
    filters: [{ field: 'tenantId', operator: 'eq', value: tenantId }],
    pagination: { mode: 'off' },
  });

  const [form, setForm] = useState(EMPTY_PET_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const pets = data?.data ?? [];

  async function add(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/pets', {
        method: 'POST',
        body: JSON.stringify({
          tenantId,
          name: form.name,
          species: form.species,
          breed: form.breed || null,
          notes: form.notes || null,
        }),
      });
      setForm(EMPTY_PET_FORM);
      await invalidate({ resource: 'pets', invalidates: ['list'] });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: PetRow) {
    if (!window.confirm(`Remove ${row.name} from this tenant's household?`)) return;
    setError(null);
    try {
      await api(`/pets/${row.id}`, { method: 'DELETE' });
      await invalidate({ resource: 'pets', invalidates: ['list'] });
    } catch (caught) {
      setError(caught);
    }
  }

  return (
    <details className="card" open={pets.length > 0}>
      <summary>Pets{pets.length > 0 ? ` (${pets.length})` : ''}</summary>

      <div className="stack">
        {isLoading ? (
          <p className="muted">Loading…</p>
        ) : pets.length === 0 ? (
          <p className="muted">No pets on file.</p>
        ) : (
          pets.map((pet) => (
            <div key={pet.id} className="row">
              <div className="stack" style={{ gap: '0.1rem' }}>
                <span>
                  <strong>{pet.name}</strong>{' '}
                  <span className="muted">
                    — {pet.species}
                    {pet.breed ? `, ${pet.breed}` : ''}
                  </span>
                </span>
                {pet.notes ? <span className="muted">{pet.notes}</span> : null}
              </div>
              <span className="shell__spacer" />
              {canWrite ? (
                <button
                  type="button"
                  className="button button--quiet"
                  onClick={() => void remove(pet)}
                >
                  Remove
                </button>
              ) : null}
            </div>
          ))
        )}

        {canWrite ? (
          <form className="stack" onSubmit={(event) => void add(event)}>
            <Field label="Name" name="petName" required>
              <TextInput
                name="petName"
                value={form.name}
                onChange={(value) => setForm((f) => ({ ...f, name: value }))}
                required
              />
            </Field>
            <Field label="Species" name="petSpecies" required hint="Dog, cat, bird…">
              <TextInput
                name="petSpecies"
                value={form.species}
                onChange={(value) => setForm((f) => ({ ...f, species: value }))}
                required
              />
            </Field>
            <Field label="Breed" name="petBreed">
              <TextInput
                name="petBreed"
                value={form.breed}
                onChange={(value) => setForm((f) => ({ ...f, breed: value }))}
              />
            </Field>
            <Field label="Notes" name="petNotes" hint="Weight, temperament, vaccination notes…">
              <TextInput
                name="petNotes"
                value={form.notes}
                onChange={(value) => setForm((f) => ({ ...f, notes: value }))}
              />
            </Field>

            {error ? (
              <p className="alert alert--error">
                {error instanceof Error ? error.message : 'Could not save.'}
              </p>
            ) : null}

            <div className="row">
              <button
                type="submit"
                className="button"
                disabled={busy || form.name === '' || form.species === ''}
              >
                {busy ? 'Adding…' : 'Add pet'}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </details>
  );
}
